/**
 * Variant Combination Filter — Shopify Impulse Theme
 *
 * Problem:  Shopify's native Search & Discovery filtering uses OR logic across
 *           variant options, so selecting Size=30 + Length=16 shows any product
 *           that has *any* Size-30 variant OR *any* Length-16 variant.
 *
 * Solution: After every filter update this script reads the selected option
 *           filters from the URL, checks each product card's embedded variant
 *           data, and hides any card that has no single variant satisfying ALL
 *           selected option values simultaneously.
 *
 * Requirements
 *   • Liquid change: add data-option-names and data-variant-options attributes
 *     to every .grid__item in snippets/product-grid-item.liquid  (see guide).
 *   • Load this file in the collection-template section or theme layout AFTER
 *     the Impulse theme JS bundle.
 *
 * Compatibility: all modern browsers; no dependencies.
 */

(function () {
  'use strict';

  /* ─── Constants ─────────────────────────────────────────────────────────── */

  // Shopify Search & Discovery uses this prefix for variant-option URL params.
  // e.g. filter.v.option.size=30   filter.v.option.length=16"
  var OPTION_PREFIX = 'filter.v.option.';

  // Sentinel attribute used to track cards hidden by this script.
  var HIDDEN_ATTR = 'data-vcf-hidden';

  // Selectors — adjust if your Impulse build uses different class names.
  var CARD_SELECTOR   = '.grid__item[data-variant-options]';
  var SECTION_SELECTOR = [
    '[data-section-type="collection-template"]',
    '[data-section-type="collection-grid"]',
    '[data-section-id="collection-template"]',
    '#CollectionSection',
    '#CollectionProductGrid',
  ].join(', ');

  /* ─── State ──────────────────────────────────────────────────────────────── */

  var filterTimer      = null;
  var sectionObserver  = null;

  /* ─── URL Parsing ────────────────────────────────────────────────────────── */

  /**
   * Returns an object mapping normalised option names → array of selected values.
   * e.g. { size: ['30'], length: ['16"'] }
   *
   * "Normalised" = lowercase, spaces converted to underscores, matching the
   * URL param suffix produced by Search & Discovery.
   */
  function getSelectedFilters() {
    var params  = new URLSearchParams(window.location.search);
    var filters = {};

    params.forEach(function (value, key) {
      if (key.indexOf(OPTION_PREFIX) !== 0) return;

      var optionKey = key.slice(OPTION_PREFIX.length); // already normalised by Shopify
      if (!filters[optionKey]) filters[optionKey] = [];
      filters[optionKey].push(value.toLowerCase().trim());
    });

    return filters;
  }

  /* ─── Variant Matching ───────────────────────────────────────────────────── */

  /**
   * Returns true if the product card contains at least one variant that
   * satisfies EVERY key in `filters` simultaneously.
   *
   * data-option-names:    JSON array of option names in definition order.
   *                       e.g. ["Waist Size","Length"]
   *
   * data-variant-options: JSON array of arrays — one inner array per variant,
   *                       values in the same order as option-names.
   *                       e.g. [["30","14\""],["30","16\""],["28","14\""]]
   */
  function cardMatchesFilters(card, filters, filterKeys) {
    var optNamesRaw   = card.getAttribute('data-option-names');
    var variantsRaw   = card.getAttribute('data-variant-options');

    // Missing data → don't hide (safe default).
    if (!optNamesRaw || !variantsRaw) return true;

    var optionNames, variants;
    try {
      optionNames = JSON.parse(optNamesRaw);
      variants    = JSON.parse(variantsRaw);
    } catch (e) {
      return true; // Malformed JSON → don't hide.
    }

    // Build a lookup: normalised name → index in the options array.
    var nameIndex = {};
    optionNames.forEach(function (name, i) {
      nameIndex[name.toLowerCase().replace(/\s+/g, '_')] = i;
    });

    // A product passes if at least one variant satisfies every active filter.
    return variants.some(function (variantOpts) {
  // Last element is availability flag — skip sold-out variants
  var available = variantOpts[optionNames.length];
  if (available === false) return false;

  return filterKeys.every(function (filterKey) {
    var idx = nameIndex[filterKey];
    if (idx === undefined) return true;
    var variantValue = (variantOpts[idx] || '').toLowerCase().trim();
    return filters[filterKey].indexOf(variantValue) !== -1;
  });
});
  }

  /* ─── DOM Filtering ──────────────────────────────────────────────────────── */

  /**
   * Main filter pass.  Called after every filter change.
   */
  function applyFilter() {
    var filters    = getSelectedFilters();
    var filterKeys = Object.keys(filters);

    // ── Step 1: Restore any cards this script previously hid. ──────────────
    document.querySelectorAll('[' + HIDDEN_ATTR + ']').forEach(function (el) {
      el.style.display = '';
      el.removeAttribute(HIDDEN_ATTR);
    });

    // ── Step 2: Only intervene when ≥2 variant option filters are active. ──
    // With a single filter Shopify's own logic already returns only products
    // that have *some* variant with that value — no extra filtering needed.
    if (filterKeys.length < 2) return;

    // ── Step 3: Hide cards with no matching variant combination. ─────────────
    document.querySelectorAll(CARD_SELECTOR).forEach(function (card) {
      if (!cardMatchesFilters(card, filters, filterKeys)) {
        card.style.display = 'none';
        card.setAttribute(HIDDEN_ATTR, '');
      }
    });
  }

  /* ─── Debouncing ─────────────────────────────────────────────────────────── */

  function scheduleFilter(delayMs) {
    clearTimeout(filterTimer);
    filterTimer = setTimeout(applyFilter, delayMs != null ? delayMs : 80);
  }

  /* ─── AJAX / History Hooks ───────────────────────────────────────────────── */

  /**
   * Intercept history.pushState so filter changes that update the URL (but
   * don't trigger popstate) still re-run the filter.
   */
  function patchHistory() {
    var originalPush = history.pushState.bind(history);
    history.pushState = function (state, title, url) {
      originalPush(state, title, url);
      scheduleFilter(150);
    };

    window.addEventListener('popstate', function () {
      scheduleFilter(150);
    });
  }

  /**
   * MutationObserver on the collection section catches Impulse's AJAX HTML
   * replacement (new product cards injected after a filter/sort/paginate).
   */
  function observeSection() {
    if (sectionObserver) sectionObserver.disconnect();

    var section = document.querySelector(SECTION_SELECTOR);
    if (!section) return;

    sectionObserver = new MutationObserver(function (mutations) {
      var hasChanges = mutations.some(function (m) {
        return m.addedNodes.length > 0 || m.removedNodes.length > 0;
      });
      if (hasChanges) scheduleFilter(120);
    });

    sectionObserver.observe(section, { childList: true, subtree: true });
  }

  /**
   * Shopify fires shopify:section:load when a section is re-rendered server-
   * side and injected.  Also covers theme editor previews.
   */
  function listenSectionLoad() {
    document.addEventListener('shopify:section:load', function (e) {
      var sectionId = e.detail && (e.detail.sectionId ||
                      (e.detail.section && e.detail.section.id) || '');
      // Re-observe so the new DOM node is tracked.
      if (sectionId === '' || sectionId.indexOf('collection') !== -1) {
        observeSection();
        scheduleFilter(50);
      }
    });
  }

  /* ─── Init ───────────────────────────────────────────────────────────────── */

  function init() {
    applyFilter();
    observeSection();
    patchHistory();
    listenSectionLoad();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
